// src/sketches/mySketch.ts

import p5 from 'p5';
import { InitialPointOfImpact } from '../../models/initial-point-of-impact';
import { DamageLocation } from '../../models/damage';
import { EllipseConfiguration } from './ellipse';
import { DamageSelection } from '../../models/collision';

export interface DamageSelectorLayoutConfigurationProps {
  canvasDefaultWidth: number;
  canvasDefaultHeight: number;
  totalCarContainerHeight: number;
  carImageWidth: number;
  carImageHeight: number;
  ellipseRadius: number;
  ellipseHorizontalSpacing: number;
  ellipseTopRow: number;
  ellipseBottomRow: number;
  ellipseMiddleRow: number;
  rearAndFrontSpacing: number;
  otherDamageLabelPadding: number;
  labelHeight: number;
  xlSpacer: number;
  fontSize: number;
}

class DamageSelectorLayoutConfiguration {
  configurationProps: DamageSelectorLayoutConfigurationProps;
  ellipsePositionConfigurations: EllipseConfiguration[];

  constructor(props: DamageSelectorLayoutConfigurationProps) {
    this.configurationProps = props;

    this.ellipsePositionConfigurations = [
      {
        description: DamageLocation.RIGHT_SIDE_FRONT,
        x:
          this.configurationProps.canvasDefaultWidth / 2 -
          this.configurationProps.ellipseHorizontalSpacing -
          this.configurationProps.ellipseRadius,
        y: this.configurationProps.ellipseTopRow,
      },
      {
        description: DamageLocation.RIGHT_SIDE_MIDDLE,
        x: this.configurationProps.canvasDefaultWidth / 2,
        y: this.configurationProps.ellipseTopRow,
      },
      {
        description: DamageLocation.RIGHT_SIDE_REAR,
        x:
          this.configurationProps.canvasDefaultWidth / 2 +
          this.configurationProps.ellipseHorizontalSpacing +
          this.configurationProps.ellipseRadius,
        y: this.configurationProps.ellipseTopRow,
      },
      {
        description: DamageLocation.LEFT_SIDE_FRONT,
        x:
          this.configurationProps.canvasDefaultWidth / 2 -
          this.configurationProps.ellipseHorizontalSpacing -
          this.configurationProps.ellipseRadius,
        y: this.configurationProps.ellipseBottomRow,
      },

      {
        description: DamageLocation.LEFT_SIDE_MIDDLE,
        x: this.configurationProps.canvasDefaultWidth / 2,
        y: this.configurationProps.ellipseBottomRow,
      },

      {
        description: DamageLocation.LEFT_SIDE_REAR,
        x:
          this.configurationProps.canvasDefaultWidth / 2 +
          this.configurationProps.ellipseHorizontalSpacing +
          this.configurationProps.ellipseRadius,
        y: this.configurationProps.ellipseBottomRow,
      },
      {
        description: DamageLocation.FRONT,
        x:
          this.configurationProps.canvasDefaultWidth / 2 -
          this.configurationProps.rearAndFrontSpacing,
        y: this.configurationProps.ellipseMiddleRow,
      },
      {
        description: DamageLocation.BACK,
        x:
          this.configurationProps.canvasDefaultWidth / 2 +
          this.configurationProps.rearAndFrontSpacing,
        y: this.configurationProps.ellipseMiddleRow,
      },
      {
        description: DamageLocation.TOP,
        x:
          this.configurationProps.canvasDefaultWidth / 2 +
          this.configurationProps.ellipseRadius * 2,
        y: this.configurationProps.ellipseMiddleRow,
      },
      {
        description: DamageLocation.ATTACHMENT,
        x: this.configurationProps.canvasDefaultWidth / 2,
        y:
          this.configurationProps.totalCarContainerHeight +
          this.configurationProps.xlSpacer +
          this.configurationProps.labelHeight +
          this.configurationProps.otherDamageLabelPadding +
          this.configurationProps.ellipseRadius,
      },
      {
        description: DamageLocation.UNDERCARRIAGE,
        x: this.configurationProps.canvasDefaultWidth / 2,
        y:
          this.configurationProps.totalCarContainerHeight +
          this.configurationProps.xlSpacer +
          this.configurationProps.labelHeight +
          this.configurationProps.otherDamageLabelPadding +
          this.configurationProps.ellipseRadius,
      },
    ];
  }
}

export class MobileDamageSelectorLayoutConfiguration extends DamageSelectorLayoutConfiguration {
  constructor(pointOfImpactList: InitialPointOfImpact[]) {
    let canvasHeight = 200;
    if (
      pointOfImpactList.some(
        (x) =>
          x.description === DamageLocation.ATTACHMENT ||
          x.description === DamageLocation.UNDERCARRIAGE,
      )
    ) {
      canvasHeight = 301;
    }

    super({
      canvasDefaultWidth: 343,
      canvasDefaultHeight: canvasHeight,
      totalCarContainerHeight: 182,
      carImageWidth: 268,
      carImageHeight: 150,
      ellipseRadius: 12,
      ellipseHorizontalSpacing: 75,
      ellipseTopRow: 20,
      ellipseBottomRow: 180,
      ellipseMiddleRow: 100,
      rearAndFrontSpacing: 155,
      otherDamageLabelPadding: 28,
      labelHeight: 20,
      xlSpacer: 32,
      fontSize: 12,
    });
  }
}

export class DesktopDamageSelectorLayoutConfiguration extends DamageSelectorLayoutConfiguration {
  constructor(pointOfImpactList: InitialPointOfImpact[]) {
    let canvasHeight = 210;
    if (
      pointOfImpactList.some(
        (x) =>
          x.description === DamageLocation.ATTACHMENT ||
          x.description === DamageLocation.UNDERCARRIAGE,
      )
    ) {
      canvasHeight = 325;
    }

    super({
      canvasDefaultWidth: 370,
      canvasDefaultHeight: canvasHeight,
      totalCarContainerHeight: 196,
      carImageWidth: 289,
      carImageHeight: 162,
      ellipseRadius: 12,
      ellipseHorizontalSpacing: 80,
      ellipseTopRow: 20,
      ellipseBottomRow: 195,
      ellipseMiddleRow: 105,
      rearAndFrontSpacing: 165,
      otherDamageLabelPadding: 28,
      labelHeight: 20,
      xlSpacer: 32,
      fontSize: 12,
    });
  }
}

class CanvasDamageSelector {
  id: string;
  p: p5;
  x: number;
  y: number;
  radius: number;
  selected: boolean;
  ellipseConfiguration?: EllipseConfiguration;
  isInitialPointOfImpact: boolean;
  clickTime: number;
  verbiage: string;
  allSelectors: CanvasDamageSelector[];
  layouConfiguration: DamageSelectorLayoutConfigurationProps;
  selectionNumber?: number | undefined;

  constructor(
    id: string,
    p: p5,
    x: number,
    y: number,
    radius: number,
    ellipseConfiguration: EllipseConfiguration,
    isInitialPointOfImpact: boolean = false,
    clickTime: number = 0,
    verbiage: string = '',
    otherSelectors: CanvasDamageSelector[] = [],
    layouConfiguration: DamageSelectorLayoutConfigurationProps,
    selectionNumber: number | undefined = undefined,
  ) {
    this.id = id;
    this.p = p;
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.selected = false;
    this.ellipseConfiguration = ellipseConfiguration;
    this.isInitialPointOfImpact = isInitialPointOfImpact;
    this.clickTime = clickTime;
    this.verbiage = verbiage;
    this.allSelectors = otherSelectors;
    this.layouConfiguration = layouConfiguration;
    this.selectionNumber = selectionNumber;
  }

  draw() {
    if (this.ellipseConfiguration) {
      if (this.selected) {
        this.p.fill(0, 0, 0);
      } else {
        this.p.fill(0, 129, 162);
      }

      //var(--goa-color-brand-default)
      if (this.isInitialPointOfImpact) this.p.fill(0, 129, 162);

      this.p.ellipse(this.x, this.y, this.radius * 2, this.radius * 2);
      this.p.noStroke();
      this.p.textAlign(this.p.CENTER, this.p.CENTER);
      this.p.textSize(this.radius / 1);

      if (this.clickTime > 1) {
        this.p.fill(255, 255, 255);

        const releventSelectors = this.allSelectors
          .filter((x) => x.clickTime > 1)
          .sort((a, b) => a.clickTime - b.clickTime);

        if (this.selectionNumber) {
          this.p.text(this.selectionNumber, this.x, this.y);
        }
      }

      if (this.verbiage.length > 0) {
        this.p.fill(0, 0, 0);
        this.p.textAlign(this.p.LEFT, this.p.CENTER);
        this.p.textSize(12);

        this.p.text(this.verbiage, this.x + this.radius + 6, this.y); // Draw the text at position (50, 100)

        if (
          this.ellipseConfiguration.description === DamageLocation.ATTACHMENT ||
          this.ellipseConfiguration.description === DamageLocation.UNDERCARRIAGE
        ) {
          this.p.text(
            'Other vehicle areas',
            this.layouConfiguration.canvasDefaultWidth / 2 -
              145 -
              this.layouConfiguration.ellipseRadius,
            this.layouConfiguration.totalCarContainerHeight +
              this.layouConfiguration.xlSpacer +
              this.layouConfiguration.labelHeight +
              this.layouConfiguration.otherDamageLabelPadding +
              this.layouConfiguration.ellipseRadius -
              this.layouConfiguration.otherDamageLabelPadding,
          ); // Draw the text at position (50, 100)
        }
      }
    }
  }

  handleClick(
    mx: number,
    my: number,
    clickTime: number,
    selections: CanvasDamageSelector[],
  ): CanvasDamageSelector | undefined {
    if (!this.isInitialPointOfImpact) {
      const d = this.p.dist(mx, my, this.x, this.y);
      if (d < this.radius && !this.selected) {
        this.selected = !this.selected; // Toggle selection

        if (this.selected) {
          this.clickTime = clickTime;
          selections.push(this);
        } else {
          this.clickTime = 0;
          const indexToRemove = selections.findIndex(
            (x) => x.id.toString() == this.id.toString(),
          ); // Find the index of the item

          if (indexToRemove !== -1) {
            selections.splice(indexToRemove, 1);
          }
        }
        return this;
      }
    }
    return undefined;
  }
}

export class DamageSketch {
  p!: p5;
  img!: p5.Image;
  p5Canvas!: p5.Renderer;
  canvasDamageSelectionOptions: CanvasDamageSelector[] = [];
  selections: CanvasDamageSelector[] = [];

  constructor() {}

  countStartingNumber: number = 0;

  getSelectedDamageLocationDescriptions = () => {
    return this.canvasDamageSelectionOptions
      .filter((selector) => selector.selected)
      .map((selector) => selector.ellipseConfiguration!.description);
  };

  handleDamageUpdate = (
    damageSelector: CanvasDamageSelector,
    handleDamageUpdate: any,
  ) => {
    handleDamageUpdate({
      pointOfImpactId: damageSelector.id.toString(),
      clickTime: damageSelector.clickTime,
      value: damageSelector.selected,
      isInitialPointOfImpact: damageSelector.isInitialPointOfImpact,
    });
  };

  removeDamageSelection = (id: string, handleDamageUpdate: any) => {
    const foundSelector = this.canvasDamageSelectionOptions.find(
      (x) => x.id == id.toString(),
    );

    const indexToRemove = this.selections.findIndex(
      (x) => x.id == id.toString(),
    );

    if (indexToRemove !== -1) {
      this.selections.splice(indexToRemove, 1);
    }

    this.canvasDamageSelectionOptions.forEach((x) => {
      if (x.id == id) {
        x.selected = false;
        x.clickTime = 0;
      }
    });

    this.handleDamageUpdate(foundSelector!, handleDamageUpdate);
  };

  public sketch =
    (
      containerDiv: HTMLDivElement,
      damageSelectionList: DamageSelection[],
      pointOfImpactLocationList: InitialPointOfImpact[], // this is a list of all the impact options loaded from the api
      countStartingNumber: number,
      handleDamageUpdate: any,
      enableClick: boolean = true,
      layoutConfiguration: DamageSelectorLayoutConfiguration,
      highlightPointOfImpact: boolean = false,
    ) =>
    (p: p5) => {
      this.p = p;
      this.countStartingNumber = countStartingNumber;
      let heightOffset = 2;

      if (
        pointOfImpactLocationList.some(
          (x) =>
            x.description === DamageLocation.ATTACHMENT ||
            x.description === DamageLocation.UNDERCARRIAGE,
        )
      ) {
        heightOffset = 3;
      }

      const setupInitialPointOfImpact = () => {
        const initialPointOfImpact = damageSelectionList.find(
          (x) => x.isInitialPointOfImpact,
        );

        if (!initialPointOfImpact) return;

        const selector = this.canvasDamageSelectionOptions.find(
          (x) => x.id === initialPointOfImpact.pointOfImpactId?.toString(),
        );

        if (selector) {
          selector.isInitialPointOfImpact = true;
          selector.selected = true;
          selector.clickTime! = damageSelectionList[0].clickTime!;
        }
      };

      const setupPointsOfImpact = () => {
        const damageLocations = [...damageSelectionList];

        damageLocations
          .sort((a, b) => a.clickTime! - b.clickTime!)
          .forEach((item, index) => {
            const selector = this.canvasDamageSelectionOptions.find(
              (x) => x.id === item.pointOfImpactId?.toString(),
            );

            if (selector) {
              selector.selected = true;
              selector.clickTime = item.clickTime!;
              selector.selectionNumber = index + 1;
            }
          });
      };

      const setupDamageSelectors = () => {
        otherDamageCount = 0;
        this.canvasDamageSelectionOptions = [];
        pointOfImpactLocationList.forEach((item, index) => {
          const configurationItem =
            layoutConfiguration.ellipsePositionConfigurations.find(
              (x) => x.description === item.description,
            );

          if (configurationItem) {
            const selection = new CanvasDamageSelector(
              item.id.toString(),
              p,
              configurationItem.x +
                calculateOtherDamageXOffset(configurationItem.description),
              configurationItem.y,
              layoutConfiguration.configurationProps.ellipseRadius,
              configurationItem,
              false,
              0,
              !item.getDamageVerbiage().showImage
                ? item.getDamageVerbiage().label
                : '',
              this.canvasDamageSelectionOptions,
              layoutConfiguration.configurationProps,
            );

            this.canvasDamageSelectionOptions.push(selection);
          }
        });
      };

      p.preload = () => {
        this.img = p.loadImage('/images/car.svg');
      };

      p.setup = () => {
        this.p5Canvas = p.createCanvas(
          layoutConfiguration.configurationProps.canvasDefaultWidth,
          layoutConfiguration.configurationProps.canvasDefaultHeight,
        );
        p.imageMode(p.CENTER);

        if (pointOfImpactLocationList && pointOfImpactLocationList.length > 0) {
          setupDamageSelectors();
          setupPointsOfImpact();
          if (highlightPointOfImpact) setupInitialPointOfImpact();
        }
      };

      p.draw = () => {
        p.background('0, 0%, 40%, 1');

        if (this.img) {
          p.image(
            this.img,
            p.width / 2,
            p.height / heightOffset,

            layoutConfiguration.configurationProps.carImageWidth,
            layoutConfiguration.configurationProps.carImageHeight,
          );
        }

        this.canvasDamageSelectionOptions.forEach((selector) => {
          selector.draw();
        });
      };

      p.mouseClicked = () => {
        if (enableClick) {
          let foundSelector: CanvasDamageSelector | undefined;

          for (const selector of this.canvasDamageSelectionOptions) {
            foundSelector = selector.handleClick(
              p.mouseX,
              p.mouseY,
              Date.now(),
              this.selections,
            );

            if (foundSelector) {
              this.handleDamageUpdate(foundSelector, handleDamageUpdate);
            }
          }

          let count = 1;
          this.canvasDamageSelectionOptions
            .sort((a, b) => a.clickTime - b.clickTime)
            .map((selector, index) => {
              if (selector.clickTime > 0) {
                selector.selectionNumber = count++;
              }
            });
        }
      };
    };
}

let otherDamageCount = 0;
const otherDamgaeXCoordinates = [-145, 20];

function calculateOtherDamageXOffset(damageLocation: DamageLocation) {
  if (
    damageLocation === DamageLocation.ATTACHMENT ||
    damageLocation === DamageLocation.UNDERCARRIAGE
  ) {
    const offset = otherDamgaeXCoordinates[otherDamageCount];
    otherDamageCount++;
    return offset;
  }

  return 0;
}
